import {
  applyNetState,
  createSimState,
  type EntityInstance,
  type GameModule,
  mergeNetEntities,
  type ParsedLevel,
  parseLevel,
  type SimPlayer,
  type SimState,
  snapshot as simSnapshot,
  tick as simTick,
} from '@md/engine';
import {
  type ClientMessage,
  COUNTDOWN_SECONDS,
  type DuoInputs,
  type GameEndMessage,
  INPUT_KEEPALIVE_TICKS,
  type Level,
  type LobbyPlayer,
  MAX_CATCHUP_TICKS,
  NEUTRAL_INPUTS,
  type NetEntity,
  type NetStatusSnapshot,
  type PauseReason,
  type PlayerIndex,
  type PlayerInputs,
  type RoomPhase,
  type ServerMessage,
  type SimStateSnapshot,
  TICK_RATE,
  withInputs,
} from '@md/shared';
import type { GameResult, GameSession } from '../game/session';
import { createNetClient, type NetClient } from './client';
import {
  createInterpolationBuffer,
  type InterpolationBuffer,
  type NetFrame,
} from './interpolation';
import { createPredictionBuffer, type PredictionBuffer, reconcile } from './prediction';

/**
 * Session RÉSEAU (spec §5.2).
 *
 * Répartition des rôles, telle que la spec la fige :
 *  - le SERVEUR est autoritaire, simule à 60 Hz et diffuse un snapshot à 20 Hz ;
 *  - le CLIENT applique ses entrées immédiatement sur son propre robot
 *    (prédiction), interpole le partenaire et les entités avec 100 ms de
 *    retard, et réconcilie à chaque snapshot (rejeu des entrées non
 *    confirmées).
 *
 * Rien de spécifique au magnétisme ici : la session manipule un `GameModule`
 * opaque et le `tick()` générique du moteur.
 */
export interface NetIdentity {
  readonly roomId: string;
  readonly code: string;
  readonly memberId: string;
  readonly token: string;
  readonly slot: 0 | 1;
  readonly host: boolean;
  readonly gameId: string;
}

export type NetEventKind =
  | 'connected'
  | 'lobby'
  | 'countdown'
  | 'gameStart'
  | 'gameEnd'
  | 'paused'
  | 'resumed'
  | 'closed'
  | 'error'
  | 'disconnected';

export interface NetSession extends GameSession {
  connect(): void;
  createRoom(gameId: string): void;
  joinRoom(code: string): void;
  rejoin(identity: Pick<NetIdentity, 'code' | 'memberId' | 'token'>): void;
  /**
   * Rouvre la socket et reprend la place courante (jeton), si elle est tombée.
   * Sans effet si la connexion est déjà ouverte ou si l'on n'est dans aucune
   * partie. C'est le chemin du retour d'un onglet mobile suspendu.
   */
  resume(): void;
  pickChar(charIndex: PlayerIndex): void;
  pickLevel(levelId: string): void;
  setReady(ready: boolean): void;
  requestPause(paused: boolean): void;
  /** Recommence le niveau en cours pour les DEUX joueurs (décompte 3-2-1). */
  requestRestart(): void;
  leave(): void;
  /** Notifié à chaque changement d'état RÉSEAU (lobby, décompte, pause…). */
  onNetChange(listener: (kind: NetEventKind, status: NetStatusSnapshot) => void): () => void;
  status(): NetStatusSnapshot;
  identity(): NetIdentity | null;
  /** Place du robot piloté par ce client (`null` avant le choix du personnage). */
  localIndex(): PlayerIndex | null;
  destroy(): void;
}

/** Source des niveaux : le catalogue servi par l'API (SQLite). */
export interface NetLevelSource {
  get(levelId: string): Level | undefined;
  ensure(levelId: string): Promise<Level | undefined>;
}

export interface NetSessionOptions {
  readonly module: GameModule;
  /** Résolution des niveaux ; sans elle, aucune partie ne peut démarrer. */
  readonly levels: NetLevelSource;
  readonly url?: string;
  readonly factory?: (url: string) => WebSocket;
  /** Horloge de rendu, injectable pour les tests (par défaut `performance.now`). */
  readonly clock?: () => number;
  /**
   * Reconnexion automatique après une coupure : délai de la 1ʳᵉ tentative
   * (doublé à chaque échec, plafonné) et nombre de tentatives. Injectables
   * pour les tests.
   */
  readonly reconnect?: {
    readonly delayMs?: number;
    readonly maxDelayMs?: number;
    readonly attempts?: number;
    readonly schedule?: (run: () => void, delayMs: number) => void;
  };
}

const TICK_MS = 1000 / TICK_RATE;

function inputsEqual(a: PlayerInputs, b: PlayerInputs): boolean {
  return (
    a.left === b.left &&
    a.right === b.right &&
    a.jump === b.jump &&
    a.action === b.action &&
    (a.moveX ?? 0) === (b.moveX ?? 0)
  );
}

/**
 * Assemble l'image à dessiner : robot local PRÉDIT, partenaire et entités
 * INTERPOLÉS. C'est l'état que voient le renderer, le HUD et `window.__md`.
 */
export function composeRenderState(
  predicted: SimState,
  frame: NetFrame | null,
  localIndex: PlayerIndex,
): SimState {
  if (!frame) {
    return predicted;
  }
  const partnerIndex: PlayerIndex = localIndex === 0 ? 1 : 0;
  const net = frame.players[partnerIndex];
  const source = predicted.players[partnerIndex];
  const partner: SimPlayer = {
    ...source,
    body: {
      ...source.body,
      x: net.x,
      y: net.y,
      vx: net.vx,
      vy: net.vy,
      onGround: net.onGround,
    },
    facing: net.facing,
    dead: net.dead,
    atExit: net.atExit,
    energy: net.energy,
    fieldActive: net.fieldActive,
    flags: { ...net.flags },
  };
  const players: readonly [SimPlayer, SimPlayer] =
    localIndex === 0 ? [predicted.players[0], partner] : [partner, predicted.players[1]];

  const byId = new Map(frame.entities.map((entity) => [entity.id, entity]));
  const entities: EntityInstance[] = predicted.entities.map((entity) => {
    const net2 = byId.get(entity.id);
    if (!net2) {
      return entity;
    }
    return {
      ...entity,
      x: net2.x,
      y: net2.y,
      vx: net2.vx,
      vy: net2.vy,
      solid: net2.solid,
      flags: { ...net2.flags },
    };
  });

  return { ...predicted, players, entities };
}

export function createNetSession(options: NetSessionOptions): NetSession {
  const { module } = options;
  const clock = options.clock ?? (() => performance.now());
  const reconnectDelayMs = options.reconnect?.delayMs ?? 800;
  const reconnectMaxDelayMs = options.reconnect?.maxDelayMs ?? 5000;
  const reconnectAttempts = options.reconnect?.attempts ?? 30;
  const schedule =
    options.reconnect?.schedule ??
    ((run: () => void, delayMs: number): void => {
      window.setTimeout(run, delayMs);
    });

  // --- État réseau ---------------------------------------------------------
  let identityValue: NetIdentity | null = null;
  let phase: RoomPhase | 'idle' = 'idle';
  let players: LobbyPlayer[] = [];
  let levelId: string | null = null;
  let countdown: number | null = null;
  let paused = false;
  let pauseReason: PauseReason | null = null;
  let connected = false;
  let lastError: { code: string; message: string } | null = null;
  let result: GameEndMessage | null = null;
  let snapshotsReceived = 0;
  let serverTick = 0;

  // --- État de simulation --------------------------------------------------
  let level: ParsedLevel | null = null;
  let predicted: SimState | null = null;
  let confirmed: SimState | null = null;
  let clientTick = 0;
  let accumulatorMs = 0;
  let awaitingFirstSnapshot = true;
  let seed = 0;
  let localInputs: PlayerInputs = NEUTRAL_INPUTS;
  let partnerInputs: PlayerInputs = NEUTRAL_INPUTS;
  let sentInputs: PlayerInputs = NEUTRAL_INPUTS;
  let ticksSinceSend = 0;
  let seq = 0;
  let netEntities: NetEntity[] = [];

  // --- Reconnexion automatique --------------------------------------------
  // Sans elle, une socket qui tombe sans que la page se ferme (onglet mobile
  // suspendu par la feuille de partage, réseau qui cligne) laissait l'écran
  // FIGÉ : les clics partaient dans une socket morte.
  let reconnectTries = 0;
  let reconnectPending = false;

  const interp: InterpolationBuffer = createInterpolationBuffer();
  const pending: PredictionBuffer = createPredictionBuffer();

  const simListeners = new Set<(snap: SimStateSnapshot) => void>();
  const netListeners = new Set<(kind: NetEventKind, status: NetStatusSnapshot) => void>();

  const me = (): LobbyPlayer | undefined =>
    players.find((player) => player.memberId === identityValue?.memberId);

  const localIndexValue = (): PlayerIndex | null => me()?.charIndex ?? null;

  const status = (): NetStatusSnapshot => ({
    mode: identityValue ? 'net' : 'idle',
    connected,
    roomId: identityValue?.roomId ?? null,
    roomCode: identityValue?.code ?? null,
    memberId: identityValue?.memberId ?? null,
    host: identityValue?.host ?? false,
    charIndex: localIndexValue(),
    phase,
    players,
    levelId,
    countdown,
    paused,
    pauseReason,
    peerConnected: players.some(
      (player) => player.memberId !== identityValue?.memberId && player.connected,
    ),
    serverTick,
    clientTick,
    pendingInputs: pending.size(),
    snapshotsReceived,
    lastError,
    result: result
      ? {
          result: result.result,
          timeMs: result.timeMs,
          stars: result.stars,
          deaths: result.deaths,
          bolts: result.bolts,
        }
      : null,
  });

  const emitNet = (kind: NetEventKind): void => {
    const snap = status();
    for (const listener of netListeners) {
      listener(kind, snap);
    }
  };

  const currentSnapshot = (): SimStateSnapshot | null => {
    const state = renderState();
    if (!state) {
      return null;
    }
    const index = localIndexValue();
    const inputs: readonly [PlayerInputs, PlayerInputs] =
      index === 0 ? [localInputs, partnerInputs] : [partnerInputs, localInputs];
    return { ...simSnapshot(state), inputs };
  };

  const emitSim = (): void => {
    const snap = currentSnapshot();
    if (!snap) {
      return;
    }
    for (const listener of simListeners) {
      listener(snap);
    }
  };

  function renderState(): SimState | null {
    if (!predicted) {
      return null;
    }
    const index = localIndexValue();
    if (index === null) {
      return predicted;
    }
    return composeRenderState(predicted, interp.sample(clock()), index);
  }

  const client: NetClient = createNetClient({
    ...(options.url === undefined ? {} : { url: options.url }),
    ...(options.factory === undefined ? {} : { factory: options.factory }),
    handlers: {
      onOpen(): void {
        connected = true;
        reconnectTries = 0;
        emitNet('connected');
      },
      onClose(): void {
        connected = false;
        emitNet('disconnected');
        scheduleResume();
      },
      onInvalid(reason: string): void {
        lastError = { code: 'BAD_MESSAGE', message: reason };
        emitNet('error');
      },
      onMessage(message: ServerMessage): void {
        handleServerMessage(message);
      },
    },
  });

  const send = (message: ClientMessage): void => {
    client.send(message);
  };

  /** Rouvre la socket et redemande SA place, jeton à l'appui. */
  function resumeNow(): void {
    const identity = identityValue;
    if (identity === null || client.isOpen() || client.isConnecting()) {
      // Une poignée de main déjà en cours porte sa propre reprise : la couper
      // pour en relancer une autre ne ferait que retarder le retour en ligne.
      return;
    }
    // `close()` purge la file d'attente : un message émis pendant la coupure
    // (un `pickChar` cliqué dans le vide) n'a plus de sens après la reprise.
    client.close();
    client.connect();
    send({
      type: 'rejoinRoom',
      code: identity.code,
      memberId: identity.memberId,
      token: identity.token,
    });
  }

  /** Replanifie une reprise, en espaçant les tentatives (borné). */
  function scheduleResume(): void {
    if (identityValue === null || reconnectPending || reconnectTries >= reconnectAttempts) {
      return;
    }
    const delay = Math.min(reconnectDelayMs * 2 ** reconnectTries, reconnectMaxDelayMs);
    reconnectTries += 1;
    reconnectPending = true;
    schedule(() => {
      reconnectPending = false;
      resumeNow();
    }, delay);
  }

  const resetGame = (): void => {
    level = null;
    predicted = null;
    confirmed = null;
    clientTick = 0;
    accumulatorMs = 0;
    awaitingFirstSnapshot = true;
    localInputs = NEUTRAL_INPUTS;
    partnerInputs = NEUTRAL_INPUTS;
    sentInputs = NEUTRAL_INPUTS;
    ticksSinceSend = 0;
    seq = 0;
    netEntities = [];
    serverTick = 0;
    snapshotsReceived = 0;
    interp.clear();
    pending.clear();
  };

  const startGame = (nextLevelId: string, nextSeed: number, startTick: number): void => {
    const source = options.levels.get(nextLevelId);
    if (!source) {
      // Le niveau n'est pas encore en mémoire : on le récupère puis on
      // recommence. Les snapshots reçus entre-temps sont ignorés et la
      // resynchronisation se fait au suivant (`awaitingFirstSnapshot`).
      void options.levels.ensure(nextLevelId).then((loaded) => {
        if (loaded && levelId === nextLevelId && level === null) {
          startGame(nextLevelId, nextSeed, startTick);
          emitNet('gameStart');
        } else if (!loaded) {
          lastError = { code: 'UNKNOWN_LEVEL', message: `niveau inconnu : ${nextLevelId}` };
          emitNet('error');
        }
      });
      levelId = nextLevelId;
      return;
    }
    resetGame();
    levelId = nextLevelId;
    seed = nextSeed;
    level = parseLevel(source, { tileLegend: module.tileLegend });
    predicted = createSimState(level, module, seed);
    confirmed = predicted;
    clientTick = startTick;
    result = null;
    // Tant que le premier snapshot n'est pas arrivé, on ne prédit rien : on
    // afficherait sinon un état qui n'a rien à voir avec l'autorité.
    awaitingFirstSnapshot = true;
  };

  function handleServerMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'connected':
        connected = true;
        emitNet('connected');
        return;

      case 'roomCreated':
      case 'roomJoined': {
        identityValue = {
          roomId: message.roomId,
          code: message.code,
          memberId: message.memberId,
          token: message.token,
          slot: message.slot,
          host: message.host,
          gameId: message.gameId,
        };
        lastError = null;
        // Nouvelle place : le résultat de la partie précédente n'a plus cours.
        result = null;
        emitNet('lobby');
        return;
      }

      case 'lobbyState': {
        phase = message.phase;
        players = [...message.players];
        levelId = message.levelId;
        paused = message.paused;
        if (!paused) {
          pauseReason = null;
        }
        if (phase !== 'countdown') {
          countdown = null;
        }
        emitNet('lobby');
        return;
      }

      case 'countdown': {
        phase = 'countdown';
        countdown = message.seconds;
        // Un décompte annonce toujours une manche neuve : le résultat de la
        // précédente (écran de victoire) n'a plus cours — cas du « Rejouer ».
        result = null;
        // Le niveau est chargé DÈS le décompte : les deux clients affichent la
        // carte figée pendant les 3-2-1, puis démarrent exactement ensemble.
        // (Comparaison faite AVANT d'écraser `levelId`, sinon une seconde
        // partie sur un autre niveau afficherait la carte précédente.)
        const needsLoad = level === null || predicted === null || level.meta.id !== message.levelId;
        levelId = message.levelId;
        if (needsLoad) {
          startGame(message.levelId, seed, 0);
        }
        emitNet('countdown');
        return;
      }

      case 'gameStart': {
        phase = 'playing';
        countdown = null;
        startGame(message.levelId, message.seed, message.startTick);
        emitNet('gameStart');
        return;
      }

      case 'snapshot': {
        if (!predicted || !level) {
          return;
        }
        serverTick = message.tick;
        snapshotsReceived += 1;
        netEntities = message.full
          ? [...message.entities]
          : mergeNetEntities(netEntities, message.entities);

        interp.push({
          tick: message.tick,
          receivedAt: clock(),
          players: message.players,
          entities: netEntities,
        });

        const index = localIndexValue();
        const partner: PlayerIndex = index === 0 ? 1 : 0;
        partnerInputs = message.players[partner].inputs;

        confirmed = applyNetState(predicted, {
          tick: message.tick,
          players: message.players,
          entities: netEntities,
          status: message.status,
          deaths: message.deaths,
          respawnTicks: message.respawnTicks,
          stars: message.stars,
        });

        if (awaitingFirstSnapshot) {
          awaitingFirstSnapshot = false;
          clientTick = message.tick;
          predicted = confirmed;
          accumulatorMs = 0;
          emitSim();
          return;
        }

        if (index !== null) {
          pending.ack(message.lastSeq[index]);
        }
        if (clientTick < message.tick) {
          clientTick = message.tick;
        }
        predicted =
          index === null
            ? confirmed
            : reconcile({
                confirmed,
                pending: pending.pending(),
                localIndex: index,
                partnerInputs,
                module,
                targetTick: clientTick,
              });
        emitSim();
        return;
      }

      case 'gameEnd': {
        result = message;
        phase = 'ended';
        emitNet('gameEnd');
        return;
      }

      case 'peerDisconnected':
        pauseReason = 'peer-disconnected';
        emitNet('paused');
        return;

      case 'peerReconnected':
        emitNet('resumed');
        return;

      case 'paused':
        paused = true;
        pauseReason = message.reason;
        emitNet('paused');
        return;

      case 'resumed':
        paused = false;
        pauseReason = null;
        accumulatorMs = 0;
        clientTick = Math.max(clientTick, message.tick);
        emitNet('resumed');
        return;

      case 'roomClosed':
        if (message.reason === 'peer-left') {
          // Départ volontaire du partenaire : la room est fermée pour tous,
          // l'application affiche pourquoi une fois revenue à l'accueil.
          lastError = { code: 'ROOM_CLOSED', message: 'Votre partenaire a quitté la partie.' };
        }
        identityValue = null;
        phase = 'idle';
        players = [];
        paused = false;
        result = null;
        resetGame();
        emitNet('closed');
        return;

      case 'error':
        if (message.code === 'NOT_IN_ROOM' && identityValue === null) {
          // Écho tardif (messages en vol pendant la fermeture de la room) :
          // le client sait déjà qu'il n'est plus en partie, rien à signaler —
          // et surtout rien qui doive écraser le message de fermeture.
          return;
        }
        lastError = { code: message.code, message: message.message };
        if (
          identityValue !== null &&
          (message.code === 'ROOM_NOT_FOUND' || message.code === 'ROOM_CLOSED')
        ) {
          // Notre place n'existe plus : on lâche l'identité au lieu de laisser
          // l'écran courant sans issue (l'application ramène à l'accueil avec
          // le message, et la reconnexion automatique s'arrête).
          identityValue = null;
          phase = 'idle';
          players = [];
          paused = false;
          result = null;
          resetGame();
        }
        emitNet('error');
        return;
    }
  }

  /** Un tick de prédiction locale : échantillonne, émet, simule. */
  const predictOneTick = (index: PlayerIndex): void => {
    if (!predicted) {
      return;
    }
    ticksSinceSend += 1;
    const changed = !inputsEqual(localInputs, sentInputs);
    if (changed || ticksSinceSend >= INPUT_KEEPALIVE_TICKS) {
      seq += 1;
      ticksSinceSend = 0;
      sentInputs = localInputs;
      pending.record({ seq, tick: clientTick, inputs: localInputs });
      send({
        type: 'input',
        seq,
        tick: clientTick,
        left: localInputs.left,
        right: localInputs.right,
        jump: localInputs.jump,
        action: localInputs.action,
        moveX: localInputs.moveX,
      });
    }
    const duo: DuoInputs =
      index === 0 ? [localInputs, partnerInputs] : [partnerInputs, localInputs];
    predicted = simTick(predicted, duo, module);
    clientTick += 1;
  };

  const session: NetSession = {
    module,

    connect(): void {
      client.connect();
    },

    createRoom(gameId: string): void {
      send({ type: 'createRoom', gameId });
    },

    joinRoom(code: string): void {
      send({ type: 'joinRoom', code });
    },

    rejoin(target): void {
      send({
        type: 'rejoinRoom',
        code: target.code,
        memberId: target.memberId,
        token: target.token,
      });
    },

    resume(): void {
      reconnectTries = 0;
      resumeNow();
    },

    pickChar(charIndex: PlayerIndex): void {
      send({ type: 'pickChar', charIndex });
    },

    pickLevel(id: string): void {
      send({ type: 'pickLevel', levelId: id });
    },

    setReady(ready: boolean): void {
      send({ type: 'ready', ready });
    },

    requestPause(value: boolean): void {
      send({ type: 'pauseRequest', paused: value });
    },

    requestRestart(): void {
      send({ type: 'restartRequest' });
    },

    leave(): void {
      send({ type: 'leaveRoom' });
      identityValue = null;
      phase = 'idle';
      players = [];
      paused = false;
      result = null;
      resetGame();
      emitNet('lobby');
    },

    state(): SimState | null {
      return renderState();
    },

    snapshot(): SimStateSnapshot | null {
      return currentSnapshot();
    },

    advance(elapsedMs: number): void {
      const index = localIndexValue();
      if (
        phase !== 'playing' ||
        paused ||
        predicted === null ||
        index === null ||
        awaitingFirstSnapshot
      ) {
        return;
      }
      accumulatorMs += Math.max(0, elapsedMs);
      let steps = Math.floor(accumulatorMs / TICK_MS);
      if (steps <= 0) {
        return;
      }
      if (steps > MAX_CATCHUP_TICKS) {
        steps = MAX_CATCHUP_TICKS;
        accumulatorMs = 0;
      } else {
        accumulatorMs -= steps * TICK_MS;
      }
      for (let i = 0; i < steps; i += 1) {
        predictOneTick(index);
      }
      emitSim();
    },

    setInputs(player: PlayerIndex, patch: Partial<PlayerInputs>): void {
      const index = localIndexValue();
      if (index !== null && player !== index) {
        throw new Error(
          `Mode réseau : ce client ne pilote que le joueur ${index} (reçu ${player}).`,
        );
      }
      localInputs = withInputs(localInputs, patch);
    },

    subscribe(listener: (snap: SimStateSnapshot) => void): () => void {
      simListeners.add(listener);
      return () => {
        simListeners.delete(listener);
      };
    },

    result(): GameResult | null {
      if (result?.result !== 'won') {
        return null;
      }
      return {
        timeMs: result.timeMs,
        deaths: result.deaths,
        bolts: result.bolts,
        stars: result.stars,
      };
    },

    onNetChange(listener): () => void {
      netListeners.add(listener);
      return () => {
        netListeners.delete(listener);
      };
    },

    status,

    identity(): NetIdentity | null {
      return identityValue;
    },

    localIndex(): PlayerIndex | null {
      return localIndexValue();
    },

    destroy(): void {
      identityValue = null;
      client.close();
      simListeners.clear();
      netListeners.clear();
      resetGame();
    },
  };

  return session;
}

/** Durée totale du décompte, exposée pour l'affichage. */
export const COUNTDOWN_TOTAL_SECONDS = COUNTDOWN_SECONDS;
